import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Content, HPData } from './home-page.data';
import { MarcheAPIService } from '../@core/api/marche-api.service';
import { ResponseWrapper } from '../@shared/models/response-wrapper.model';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AnalyticsService } from '../@core/analytics.service';
import { ApplicationRouterService } from '../@core/application-router/application-router.service';
import Swiper from 'swiper';

@Injectable({
    providedIn: 'root',
})
export class HomePageService {
    static readonly HP_DATA_API = '/hp';
    homepageData: HPData[] = [];
    currentPage = -1;
    isbrowser: boolean;
    swiperInstance: Swiper | null = null;

    private pageScrollPositions: { [page: number]: number } = {};

    private layoutBasedFlex: Record<string, string> = {
        center: '0 0 40%',

        'start-center': '0 0 100%',
        'start-end': '0 0 100%',
        'center-center': '0 0 100%',
        'end-start': '0 0 100%',
        'end-center': '0 0 100%',
        'end-end': '0 0 100%',
        'center-end': '0 0 100%',
        'center-start': '0 0 100%',

        default: '0 0 40%',
    };
    private layoutBasedOrder: Record<string, string> = {
        'start-end-left': '1',
        'start-end-center': '1',
        'end-end-left': '1',
        'end-end-center': '1',
        'center-end-center': '1',
        'center-end-left': '1',

        // 'center-end': '1',
        // 'end-center': '1',
        //
        // 'center-start': '0',
        default: '0',
    };

    private layoutBasedAlign: Record<string, string> = {
        // first row
        'start-start': 'start start',
        'start-start-center': 'start center',
        'start-start-right': 'start end',

        'start-center': 'start center',

        'start-end': 'start end',
        'start-end-center': 'start center',
        'start-end-left': 'start start',

        // second row
        'center-start': 'center start',
        'center-start-center': 'center center',
        'center-start-right': 'center end',

        'center-end': 'center end',
        'center-end-center': 'center center',
        'center-end-left': 'center start',

        // last row
        'end-start': 'end start',
        'end-start-center': 'end center',
        'end-start-right': 'end end',

        'end-center': 'end center',

        'end-end-left': 'end start',
        'end-end-center': 'end center',
        'end-end': 'end end',

        default: 'center center',
    };

    constructor(
        private marcheAPIService: MarcheAPIService,
        private analyticsService: AnalyticsService,
        @Inject(PLATFORM_ID) private platformId: object,
        private applicationRouterService: ApplicationRouterService
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.isbrowser = true;
        } else {
            this.isbrowser = false;
        }
    }

    getCurrentPage() {
        return this.currentPage;
    }

    getHomePageData(filter: any): Observable<any> {
        if (+filter?.page <= this.currentPage) {
            this.applicationRouterService.differNavApiCall.emit({
                navCall: true,
                pageId: '/',
            });
            return of({
                success: true,
                data: this.homepageData,
            });
        } else {
            if (this.analyticsService.isBot(window.navigator.userAgent)) {
                filter = { ...filter, page: -1 };
            }
            return this.marcheAPIService
                .getCDNRequest<ResponseWrapper<HPData[]> | null>(
                    HomePageService.HP_DATA_API,
                    filter
                )
                .pipe(
                    tap((res: any) => {
                        this.currentPage = +filter?.page;
                        this.homepageData.push(...res.data);
                        console.log(this.currentPage, 'page');
                        if (this.currentPage === 0) {
                            this.applicationRouterService.differNavApiCall.emit(
                                { navCall: true, pageId: '/' }
                            );
                        }
                    })
                );
        }
    }

    onSwiper(value: any) {
        this.swiperInstance = value;
        if (this.swiperInstance?.isBeginning) {
            this.swiperInstance.navigation.prevEl.style.opacity = '0';
        }
        if (this.swiperInstance?.isEnd) {
            this.swiperInstance.navigation.nextEl.style.opacity = '0';
        }
    }

    onSlideChange(event: any) {
        if (event.isEnd) {
            event.navigation.nextEl.style.opacity = '0';
            event.navigation.prevEl.style.opacity = '0.8';
        }
        if (event?.isBeginning) {
            event.navigation.nextEl.style.opacity = '0.8';
            event.navigation.prevEl.style.opacity = '0';
        }
        if (event.isBeginning === false && event.isEnd === false) {
            event.navigation.nextEl.style.opacity = '0.8';
            event.navigation.prevEl.style.opacity = '0.8';
        }
    }

    convertWebToMp4(link: string): string {
        const links = link.split('.');
        links[links.length - 1] = 'mp4';
        let newLink = '';
        newLink = links.join('.');
        newLink = newLink + '?tr=q-70';
        return newLink;
    }

    getFlex(item: Content): string {
        return (
            this.layoutBasedFlex[item.alignment] || this.layoutBasedFlex.default
        );
    }

    getFlexOrder(item: Content) {
        return (
            this.layoutBasedOrder[item?.alignment] ||
            this.layoutBasedOrder.default
        );
    }

    getFlexAlign(item: Content) {
        return (
            this.layoutBasedAlign[item?.alignment] ||
            this.layoutBasedAlign.default
        );
    }
    isAlignmentValid(alignment: string): string {
        switch (alignment) {
            case 'start-start-center':
            case 'start-start-right':
            case 'end-start-center':
            case 'end-start-right':
                return '48px 48px 48px 0';
            case 'start-end-center':
            case 'start-end-left':
            case 'end-end-center':
            case 'end-end-left':
                return '48px 0 48px 48px';
            default:
                return '48px';
        }
    }

    setScrollPosition(page: number, scrollPos: number) {
        this.pageScrollPositions[page] = scrollPos;
    }

    getScrollPosition(page: number): number {
        return this.pageScrollPositions[page++];
    }

    detectBotUser(): boolean {
        const userAgent = navigator.userAgent.toLowerCase();
        return /bot|crawler|spider|crawling/.test(userAgent);
    }
}
